import React, { useState, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import MediaFiles from '../LibraryPanel/MediaFiles/MediaFiles';
import CircularProgress from '../../UI/CircularProgress';
import MediaCropTrimModal from './MediaCropTrimModal';
import MediaGenerateTab from './MediaGenerateTab';
import CustomDropdown from '../../UI/CustomDropdown';
import { mediaAPI } from '../../../services/api';
import { Search, X, Sparkles } from 'lucide-react';
import dropdownIcon from '../../../assets/dropdown-icon.svg';

// Media types that support AI generation
const GENERATABLE_TYPES = ['image', 'video', 'audio'];

// MediaSelectorModal now uses forwardRef and manages its own state to prevent re-renders
const MediaSelectorModal = forwardRef((props, ref) => {
  // Internal state management for complete isolation
  const [modalState, setModalState] = useState({
    isOpen: false,
    mediaType: 'image',
    currentValue: null,
    onSelectCallback: null,
    opts: {}
  });
  const [activeTab, setActiveTab] = useState('library');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null); // null | 'converting'
  const [dragActive, setDragActive] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInputValue, setSearchInputValue] = useState('');
  const [activeSource, setActiveSource] = useState('all');

  // Ref for crop/trim modal
  const cropTrimRef = useRef(null);

  const canGenerate = GENERATABLE_TYPES.includes(modalState.mediaType);

  // Expose imperative API to parent
  useImperativeHandle(ref, () => ({
    open: (mediaType, currentValue, onSelectCallback, opts = {}) => {
      setModalState({
        isOpen: true,
        mediaType: mediaType || 'image',
        currentValue,
        onSelectCallback,
        opts
      });
      // Clear search when opening modal
      setSearchQuery('');
      setSearchInputValue('');
      setActiveSource('all');
      setActiveTab('library');
    },
    close: () => {
      setModalState(prev => ({ ...prev, isOpen: false }));
    }
  }), []);

  // Internal handlers that don't change
  const handleClose = useCallback(() => {
    setModalState(prev => ({ ...prev, isOpen: false }));
    // Clear search when closing modal
    setSearchQuery('');
    setSearchInputValue('');
    if (props.onClose) props.onClose();
  }, [props.onClose]);

  const handleSelect = useCallback((url, name, thumbnailUrl) => {
    setModalState(prev => {
      const mediaType = prev.mediaType;
      if ((mediaType === 'image' || mediaType === 'video') && cropTrimRef.current) {
        // Open crop/trim modal — keep media selector open behind it
        // Pass the stock thumbnail so skip/apply can include it in the result
        cropTrimRef.current.open(mediaType, url, (result) => {
          // result is either a plain URL string or an object with { url, thumbnailUrl }
          if (prev.onSelectCallback) {
            prev.onSelectCallback(result, name);
          }
          setModalState(p => ({ ...p, isOpen: false }));
        }, { ...(prev.opts || {}), stockThumbnailUrl: thumbnailUrl || null, layerKey: prev.opts?.layerKey });
        return prev; // Don't close the media selector yet
      }
      // For audio or others, pass through directly
      if (prev.onSelectCallback) {
        prev.onSelectCallback(url, name);
      }
      return { ...prev, isOpen: false };
    });
  }, []);

  // Handle file upload
  const handleFileUpload = async (file) => {
    if (!file) return;

    // Detect if this file needs server-side conversion (MOV, AVI, MPEG)
    const needsConversion = /\.(mov|avi|mpeg)$/i.test(file.name) ||
      ['video/quicktime', 'video/x-msvideo', 'video/mpeg'].includes(file.type);

    setIsUploading(true);
    setUploadProgress(0);
    setUploadStatus(null);

    try {
      const response = await mediaAPI.uploadMedia(
        file,
        {}, // empty data object
        (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
          // When upload reaches 100% and file needs conversion, show converting status
          if (percentCompleted >= 100 && needsConversion) {
            setUploadStatus('converting');
          }
        }
      );

      console.log("Upload response:", response);

      if (response.data) {
        // Handle new media API response format
        const media = response.data.data?.media || response.data;
        let uploadedUrl = media.url || media.path || media.filename;

        // Prepend the server URL since files are served from the backend
        if (uploadedUrl && uploadedUrl.startsWith("/")) {
          // Use the API base URL without the /api/v1 part
          const serverBaseURL = process.env.REACT_APP_API_URL
            ? process.env.REACT_APP_API_URL.replace("/api/v1", "")
            : "http://localhost:8000";
          uploadedUrl = `${serverBaseURL}${uploadedUrl}`;
        }

        handleSelect(uploadedUrl);
      }
    } catch (error) {
      console.error("Upload error:", error);
      alert("Failed to upload file. Please try again.");
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      setUploadStatus(null);
    }
  };

  // Handle drag and drop
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  }, []);

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
    }
  };

  // Handle media selection from MediaFiles - memoize to prevent MediaFiles re-renders
  const handleMediaSelect = useCallback((item) => {
    // MediaFiles passes the item when clicked, we pass URL, name, and thumbnail
    if (item && item.url) {
      handleSelect(item.url, item.name || item.description || '', item.thumbnail || null);
    }
  }, [handleSelect]);

  // Handle search key press
  const handleSearchKeyDown = (event) => {
    if (event.key === 'Enter') {
      setSearchQuery(searchInputValue);
    }
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchInputValue('');
    setSearchQuery('');
  };

  if (!modalState.isOpen) return null;

  const mediaTypeLabel = modalState.mediaType === 'gif'
    ? 'GIF / Sticker'
    : modalState.mediaType.charAt(0).toUpperCase() + modalState.mediaType.slice(1);

  const content = (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
      <div className="bg-ag-black-800 rounded-lg shadow-xl w-[600px] h-[600px] flex flex-col border border-ag-black-600">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-ag-black-600">
          <h2 className="text-lg font-semibold text-white">
            Select {mediaTypeLabel}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs and Search */}
        <div className="border-b border-ag-black-600">
          <div className="flex">
            <button
              onClick={() => setActiveTab("library")}
              className={`px-6 py-2 text-sm font-medium transition-colors ${activeTab === "library"
                ? "text-ag-green border-b-2 border-ag-green"
                : "text-gray-400 hover:text-white"
                }`}
            >
              Media Library
            </button>
            {canGenerate && (
              <button
                onClick={() => setActiveTab("generate")}
                className={`px-6 py-2 text-sm font-medium transition-colors flex items-center gap-1.5 ${activeTab === "generate"
                  ? "text-ag-green border-b-2 border-ag-green"
                  : "text-gray-400 hover:text-white"
                  }`}
              >
                <Sparkles className="w-3.5 h-3.5" />
                Generate {mediaTypeLabel}
              </button>
            )}
            <button
              onClick={() => setActiveTab("upload")}
              className={`px-6 py-2 text-sm font-medium transition-colors ${activeTab === "upload"
                ? "text-ag-green border-b-2 border-ag-green"
                : "text-gray-400 hover:text-white"
                }`}
            >
              Upload New
            </button>
          </div>

          {/* Search Bar and Source Filter - only show in library tab */}
          {activeTab === "library" && (
            <div className="p-3 bg-ag-black-800">
              <div className="flex gap-2 items-center">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    value={searchInputValue}
                    onChange={(e) => setSearchInputValue(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    placeholder={`Search ${modalState.mediaType}s`}
                    className="w-full pl-10 pr-10 py-2 bg-ag-black-900 border border-ag-black-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:border-ag-green transition-colors"
                  />
                  {searchInputValue && (
                    <button
                      onClick={handleClearSearch}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                      title="Clear search"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                {/* Source Filter Dropdown */}
                <CustomDropdown
                  trigger={
                    <button
                      className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-md border transition-colors ${
                        activeSource !== 'all'
                          ? 'bg-ag-green text-black border-ag-green font-medium'
                          : 'bg-ag-black-900 text-gray-300 border-ag-black-600 hover:border-ag-green'
                      }`}
                      title="Filter by source"
                    >
                      <img src={dropdownIcon} alt="" className={`w-3 h-auto ${activeSource !== 'all' ? 'invert' : ''}`} />
                      <span className="whitespace-nowrap">
                        {activeSource === 'all' ? 'All' :
                         activeSource === 'uploads' ? 'Uploads' :
                         activeSource === 'ai' ? 'AI' : 'Stock'}
                      </span>
                    </button>
                  }
                  items={[
                    { id: 'all', label: 'All Sources' },
                    { id: 'uploads', label: 'My Uploads' },
                    { id: 'ai', label: 'AI Generated' },
                    { id: 'stock', label: 'Stock Media' },
                  ]}
                  onItemClick={(item) => setActiveSource(item.id)}
                  position="bottom-right"
                />
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden p-2 bg-ag-black-900">
          {activeTab === "library" ? (
            <div className="h-full">
              <MediaFiles
                onItemSelect={handleMediaSelect}
                initialMediaType={modalState.mediaType}
                isModal={true}
                searchQuery={searchQuery}
                activeSource={activeSource}
              />
            </div>
          ) : activeTab === "generate" ? (
            <MediaGenerateTab
              mediaType={modalState.mediaType}
              onSelect={handleSelect}
            />
          ) : (
            <div className="h-full flex items-center justify-center">
              {isUploading ? (
                // Show circular progress when uploading
                <div className="flex flex-col items-center">
                  <CircularProgress
                    progress={uploadStatus === 'converting' ? 100 : uploadProgress}
                    size={120}
                    strokeWidth={8}
                  />
                  {uploadStatus === 'converting' && (
                    <p className="mt-2 text-sm text-ag-black-300 font-medium animate-pulse">Converting video…</p>
                  )}
                  <button
                    onClick={() => {
                      setIsUploading(false);
                      setUploadProgress(0);
                      setUploadStatus(null);
                    }}
                    className="mt-4 px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                // Show upload zone when not uploading
                <div
                  className={`w-full max-w-md p-8 border-2 border-dashed rounded-lg transition-colors ${dragActive
                    ? "border-ag-green bg-ag-black-800"
                    : "border-gray-600"
                    }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  <input
                    type="file"
                    id="file-upload"
                    className="hidden"
                    onChange={handleFileSelect}
                    accept={
                      modalState.mediaType === 'image' ? 'image/*' :
                        modalState.mediaType === 'gif' ? 'image/gif,image/webp' :
                          modalState.mediaType === 'video' ? 'video/*' :
                            modalState.mediaType === 'audio' ? 'audio/*' : '*'
                    }
                  />

                  <label
                    htmlFor="file-upload"
                    className="flex flex-col items-center cursor-pointer"
                  >
                    <svg
                      className="w-12 h-12 text-gray-400 mb-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                      />
                    </svg>

                    <p className="text-white text-center mb-2">
                      {dragActive
                        ? "Drop file here"
                        : "Drag & drop or click to upload"}
                    </p>

                    <p className="text-gray-400 text-sm text-center">
                      Supported formats: {
                        modalState.mediaType === 'image' ? 'JPG, PNG, GIF, SVG' :
                          modalState.mediaType === 'gif' ? 'GIF, WebP' :
                            modalState.mediaType === 'video' ? 'MP4, WebM, OGG' :
                              modalState.mediaType === 'audio' ? 'MP3, WAV, OGG' : 'All files'
                      }
                    </p>
                  </label>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Current Value Display */}
        {modalState.currentValue && (
          <div className="px-4 pb-4">
            <div className="text-xs text-gray-400 flex items-center gap-1">
              <span className="flex-shrink-0">Current:</span>
              <span className="text-white truncate min-w-0">{modalState.currentValue.split('/').pop()}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      {content}
      {/* Crop/Trim modal renders on top when opened */}
      <MediaCropTrimModal ref={cropTrimRef} />
    </>
  );
});

MediaSelectorModal.displayName = 'MediaSelectorModal';

export default MediaSelectorModal;
